import React, {useEffect, useState} from "react";
import {Table, Input, Flex, Segmented, Tag, Button} from "antd";
import {saveAs} from "file-saver";
import {DownloadOutlined, SearchOutlined} from "@ant-design/icons";
import {HttpService} from "../../services/http/Http.service";
import {ALERT_TYPE_LABELS, AlertStatus, AlertType, NonPaymentAlertSimpleDto} from "../../model/models";
import {HttpErrorService} from "../../services/http/HttpError.service";
import UserDetailsButton from "../userDetails/UserDetailsButton";
import Suspend from "../../components/suspend/Suspend";
import {SUSPENSION_REASON_LABELS} from "../../model/models";

export default function NonPaymentAlertPage() {
    const [alerts, setAlerts] = useState<NonPaymentAlertSimpleDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedAlerts, setSelectedAlerts] = useState<NonPaymentAlertSimpleDto[]>([]);

    const status = AlertStatus.CREATO;
    const [typeFilter, setTypeFilter] = useState<AlertType | "ALL">("ALL");
    const [exporting, setExporting] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchText);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchText]);

    useEffect(() => {
        loadAlerts(status, debouncedSearch);
    }, [status, debouncedSearch, currentPage, typeFilter]);

    const loadAlerts = async (status: AlertStatus, query?: string) => {
        setLoading(true);
        try {
            const params = {
                page: currentPage - 1,
                size: pageSize,
                query: query || "",
                ...(typeFilter !== "ALL" ? {type: typeFilter} : {}),
            };
            const response = await HttpService.getInstanceAxios().get(`/alert/non-payment/status/${status}`, {params});
            setAlerts(response.data.content);
            setTotalItems(response.data.totalElements);
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero degli alert", error, true);
        } finally {
            setLoading(false);
        }
    };

    const exportExcel = async () => {
        setExporting(true);
        try {
            const params = {
                query: debouncedSearch || "",
                ...(typeFilter !== "ALL" ? {type: typeFilter} : {}),
            };
            const response = await HttpService.getInstanceAxios().get(`/alert/non-payment/export/${status}`, {
                params,
                responseType: "blob",
            });
            saveAs(
                new Blob([response.data], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}),
                "avvisi_mancato_pagamento.xlsx"
            );
        } catch (error) {
            HttpErrorService.handle("Errore durante l'esportazione degli alert", error, true);
        } finally {
            setExporting(false);
        }
    };

    const columns = [
        {
            title: "Codice Fiscale",
            dataIndex: "fiscalCode",
            key: "fiscalCode",
        },
        {
            title: "Cognome Nome",
            dataIndex: "fullName",
            key: "fullName",
        },
        {
            title: "Protocollo di archiviazione",
            dataIndex: "archiveProtocol",
            key: "archiveProtocol",
            render: (text: string | null) => text || "-",
        },
        {
            title: "Tipo",
            dataIndex: "type",
            key: "type",
            render: (type: AlertType) => (
                <Tag color={type === AlertType.A_VALLE ? "orange" : "blue"}>{ALERT_TYPE_LABELS[type] ?? "-"}</Tag>
            ),
        },
        {
            title: "Scadenza rata",
            dataIndex: "deadline",
            key: "deadline",
            render: (text: string) => new Date(text).toLocaleDateString("it-IT"),
        },
        {
            title: "Azione",
            key: "action",
            render: (record: NonPaymentAlertSimpleDto) => (
                <UserDetailsButton personID={record.personId} combinatorID={record.simulatorId}/>
            ),
        },
    ];

    return (
        <>
            <Flex vertical gap={12} style={{margin: "16px 0"}}>
            <Segmented
                value={typeFilter}
                options={[
                    {label: "Tutti", value: "ALL"},
                    {label: ALERT_TYPE_LABELS[AlertType.A_MONTE], value: AlertType.A_MONTE},
                    {label: ALERT_TYPE_LABELS[AlertType.A_VALLE], value: AlertType.A_VALLE},
                ]}
                onChange={(value) => {
                    setCurrentPage(1);
                    setSelectedRowKeys([]);
                    setSelectedAlerts([]);
                    setTypeFilter(value as AlertType | "ALL");
                }}
            />
            <div className="container-search">
                <Input
                    size="large"
                    style={{width: "100%", maxWidth: "480px"}}
                    allowClear
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    addonBefore={<SearchOutlined/>}
                    placeholder="Nome, Cognome, Codice Fiscale, Numero Protocollo"
                />
            </div>
            </Flex>

            <Table
                rowKey={(record) => record.id}
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys, rows) => {
                        setSelectedRowKeys(keys);
                        setSelectedAlerts(rows);
                    },
                }}
                columns={columns}
                dataSource={alerts}
                loading={loading}
                pagination={{
                    showSizeChanger: false,
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalItems,
                    onChange: (page) => setCurrentPage(page),
                }}
            />

            <Flex justify={"flex-end"} style={{gap: '5px'}}>
                <Button  size="large" icon={<DownloadOutlined/>} loading={exporting} onClick={exportExcel}>
                    Esporta
                </Button>
                <Suspend selectedSimulatorPersonPairs={selectedAlerts.map((alert) => ({
                    simulatorId: alert.simulatorId,
                    personId: alert.personId,
                }))} onSuccess={() => {
                    setSelectedRowKeys([]);
                    setSelectedAlerts([]);
                    loadAlerts(status, debouncedSearch)
                }} reason={SUSPENSION_REASON_LABELS.RATA_NON_PAGATA}/>
            </Flex>
        </>
    );
}
